import React from 'react';
import { useLocation } from 'react-router-dom';
import { JSMABridge } from '@indeed/jsma-js-bridge';
import { Box, TextLink } from '@indeed/ifl-components';
import { Report } from '@indeed/ifl-icons';
import useGettext from '../../../util/GettextFactory';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { usePageFlow } from '../../../lib/pageFlow';

/**
 * Link that opens the mid-setup feedback modal via JSMABridge.
 * In a native app context, the feedback flow is opened as a bottom-sheet webview modal
 * using showModalWithWebView (fire-and-forget, no result needed).
 * On web (non-native), uses client-side React Router navigation.
 *
 * Passes the main session's setupLogUid as a URL parameter so that
 * feedback events logged in a modal webview (which gets its own setupLogUid)
 * can still be correlated with the originating setup session.
 */
const MidSetupFeedbackLink: React.FC = () => {
    const gt = useGettext();
    const location = useLocation();
    const { enterSubFlow } = usePageFlow();
    const { setupLogUid } = useInitialState();

    // currentPage records the originating setup page for the feedback analytics
    // event (read in MidSetupFeedbackDetailsStep). It is NOT used for navigation:
    // returning to the origin is handled by the sub-flow (subFlowOrigin). It still
    // needs to travel on the URL because the native modal opens in a separate
    // webview with no in-app history to derive the origin from. See IAFE-383.
    const currentPage = location.pathname + location.search;
    const params = new URLSearchParams();
    params.set('currentPage', currentPage);
    if (setupLogUid) {
        params.set('setupLogUid', setupLogUid);
    }
    const feedbackPath = `/auto-apply-setup/mid-setup-feedback?${params.toString()}`;

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (JSMABridge.isAvailable()) {
            const feedbackUrl = new URL(feedbackPath, window.location.href).href;
            void JSMABridge.showModalWithWebView({
                url: feedbackUrl,
                title: gt.gettext('Report an issue'),
                presentationMode: 'FULLSCREEN',
            });
        } else {
            // Web: open the feedback detour as a sub-flow. The current setup step
            // is recorded as the origin so exitSubFlow() pops cleanly back to it —
            // no return param needed. feedbackPath's analytics params
            // (currentPage/setupLogUid) are preserved by enterSubFlow. On native
            // (above) the modal is loaded directly with no in-app state; the detour
            // is still recognised by its route, so its steps navigate correctly and
            // exit falls back to the fallbackRoute. See IAFE-383.
            void enterSubFlow(feedbackPath);
        }
    };

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Report size="sm" aria-hidden="true" />
            <TextLink
                href={feedbackPath}
                onClick={handleClick}
                variant="neutral"
                sx={{ fontSize: 2 }}
            >
                {gt.gettext('Report an issue')}
            </TextLink>
        </Box>
    );
};

export default MidSetupFeedbackLink;
